import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Server, Wifi, Camera, Monitor } from 'lucide-react';

export function BlueprintAnimation() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });

  // Animation variants
  const drawLine = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: (i: number) => ({
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { delay: i * 0.5, type: "spring", duration: 1.5, bounce: 0 },
        opacity: { delay: i * 0.5, duration: 0.1 }
      }
    })
  };

  const dropIcon = {
    hidden: { y: -20, opacity: 0 },
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: { delay: i * 0.5 + 0.5, type: "spring", bounce: 0.5 }
    })
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full max-w-5xl mx-auto h-[400px] bg-[#0A192F] rounded-2xl border border-blue-900/50 overflow-hidden shadow-2xl"
      style={{
        backgroundImage: `
          linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px'
      }}
    >
      {/* Floorplan Watermark (Faint Walls) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20" viewBox="0 0 1000 400" preserveAspectRatio="xMidYMid slice">
        <g stroke="#3B82F6" strokeWidth="4" fill="none" strokeLinecap="square">
          {/* Outer Walls */}
          <rect x="50" y="50" width="900" height="300" />
          {/* Inner Room 1 (Server Room) */}
          <rect x="420" y="120" width="160" height="160" />
          {/* Hallways / Dividers */}
          <path d="M 50 200 L 420 200 M 580 200 L 950 200 M 300 50 L 300 150 M 700 250 L 700 350" />
          {/* Doors (gaps in walls, simulated by covering with background color) */}
        </g>
      </svg>

      {/* SVG Layer for Connections and Pulses */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 400" preserveAspectRatio="xMidYMid slice">
        <defs>
          {/* Glowing particle effect */}
          <filter id="pulseGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* --- CONNECTION LINES --- */}
        {/* Line to WiFi (Top Left) */}
        <motion.path
          d="M 500 200 L 200 200 L 200 100"
          fill="none"
          stroke="#3B82F6"
          strokeWidth="2"
          strokeDasharray="4 4"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          custom={1}
          variants={drawLine}
        />
        {/* Line to Camera (Top Right) */}
        <motion.path
          d="M 500 200 L 800 200 L 800 100"
          fill="none"
          stroke="#10B981"
          strokeWidth="2"
          strokeDasharray="4 4"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          custom={2}
          variants={drawLine}
        />
        {/* Line to Workstation (Bottom Left) */}
        <motion.path
          d="M 500 200 L 300 200 L 300 320"
          fill="none"
          stroke="#F97316"
          strokeWidth="2"
          strokeDasharray="4 4"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          custom={3}
          variants={drawLine}
        />
        {/* Line to Access Control (Bottom Right) */}
        <motion.path
          d="M 500 200 L 700 200 L 700 300"
          fill="none"
          stroke="#8B5CF6"
          strokeWidth="2"
          strokeDasharray="4 4"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          custom={4}
          variants={drawLine}
        />

        {/* --- PULSING DATA PACKETS --- */}
        {isInView && (
          <g filter="url(#pulseGlow)">
            {/* Pulse to WiFi */}
            <circle r="4" fill="#60A5FA">
              <animateMotion dur="2s" begin="1.5s" repeatCount="indefinite" path="M 500 200 L 200 200 L 200 100" />
            </circle>
            {/* Pulse to Camera */}
            <circle r="4" fill="#34D399">
              <animateMotion dur="2.5s" begin="2s" repeatCount="indefinite" path="M 500 200 L 800 200 L 800 100" />
            </circle>
            <circle r="4" fill="#34D399">
              <animateMotion dur="2.5s" begin="3s" repeatCount="indefinite" path="M 800 100 L 800 200 L 500 200" />
            </circle>
            {/* Pulse to Workstation */}
            <circle r="4" fill="#FB923C">
              <animateMotion dur="1.8s" begin="2.5s" repeatCount="indefinite" path="M 500 200 L 300 200 L 300 320" />
            </circle>
            <circle r="4" fill="#FB923C">
              <animateMotion dur="1.8s" begin="3.2s" repeatCount="indefinite" path="M 300 320 L 300 200 L 500 200" />
            </circle>
            {/* Pulse to Access Control */}
            <circle r="4" fill="#A78BFA">
              <animateMotion dur="2.2s" begin="3s" repeatCount="indefinite" path="M 500 200 L 700 200 L 700 300" />
            </circle>

            {/* Ambient Node Pulses */}
            <circle cx="200" cy="100" r="8" fill="none" stroke="#3B82F6" strokeWidth="2">
              <animate attributeName="r" values="8;20" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx="800" cy="100" r="8" fill="none" stroke="#10B981" strokeWidth="2">
              <animate attributeName="r" values="8;20" dur="2s" begin="0.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0" dur="2s" begin="0.5s" repeatCount="indefinite" />
            </circle>
            <circle cx="300" cy="320" r="8" fill="none" stroke="#F97316" strokeWidth="2">
              <animate attributeName="r" values="8;20" dur="2s" begin="1s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0" dur="2s" begin="1s" repeatCount="indefinite" />
            </circle>
            <circle cx="700" cy="300" r="8" fill="none" stroke="#8B5CF6" strokeWidth="2">
              <animate attributeName="r" values="8;20" dur="2s" begin="1.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0" dur="2s" begin="1.5s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
      </svg>

      {/* --- HTML OVERLAY NODES --- */}
      {/* Central Server Rack */}
      <motion.div
        className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-10"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        custom={0}
        variants={dropIcon}
      >
        <div className="bg-navy-900 p-4 rounded-xl border border-cobalt-500 shadow-[0_0_20px_rgba(59,98,240,0.4)]">
          <Server className="w-10 h-10 text-cobalt-400" />
        </div>
        <span className="text-cobalt-300 text-xs font-mono mt-2 uppercase tracking-widest bg-navy-950/80 px-2 rounded backdrop-blur-sm border border-cobalt-500/30">MDF / Core</span>
      </motion.div>

      {/* WiFi Node */}
      <motion.div
        className="absolute top-[25%] left-[20%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        custom={1.5}
        variants={dropIcon}
      >
        <div className="bg-navy-900 p-3 rounded-xl border border-blue-500 shadow-lg relative z-10">
          <Wifi className="w-6 h-6 text-blue-400" />
        </div>
        <span className="text-blue-300 text-[10px] font-mono mt-2 uppercase tracking-widest bg-navy-950/80 px-2 rounded backdrop-blur-sm border border-blue-500/30 whitespace-nowrap">UniFi U7-Pro</span>
      </motion.div>

      {/* Camera Node */}
      <motion.div
        className="absolute top-[25%] left-[80%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        custom={2.5}
        variants={dropIcon}
      >
        <div className="bg-navy-900 p-3 rounded-xl border border-emerald-500 shadow-lg relative z-10">
          <Camera className="w-6 h-6 text-emerald-400" />
        </div>
        <span className="text-emerald-300 text-[10px] font-mono mt-2 uppercase tracking-widest bg-navy-950/80 px-2 rounded backdrop-blur-sm border border-emerald-500/30 whitespace-nowrap">Protect G5</span>
      </motion.div>

      {/* Workstation Node */}
      <motion.div
        className="absolute top-[80%] left-[30%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        custom={3.5}
        variants={dropIcon}
      >
        <div className="bg-navy-900 p-3 rounded-xl border border-safety-orange shadow-lg relative z-10">
          <Monitor className="w-6 h-6 text-safety-orange" />
        </div>
        <span className="text-orange-300 text-[10px] font-mono mt-2 uppercase tracking-widest bg-navy-950/80 px-2 rounded backdrop-blur-sm border border-orange-500/30 whitespace-nowrap">Cat6A Certified</span>
      </motion.div>

      {/* Device Node (Access Control) */}
      <motion.div
        className="absolute top-[75%] left-[70%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        custom={4.5}
        variants={dropIcon}
      >
        <div className="bg-navy-900 p-3 rounded-xl border border-purple-500 shadow-lg relative z-10">
          <div className="w-6 h-6 flex items-center justify-center font-bold text-purple-400 text-[10px]">PoE</div>
        </div>
        <span className="text-purple-300 text-[10px] font-mono mt-2 uppercase tracking-widest bg-navy-950/80 px-2 rounded backdrop-blur-sm border border-purple-500/30 whitespace-nowrap">Access Reader Pro</span>
      </motion.div>

    </div>
  );
}
